import { ShiftComparison } from 'src/helper/quoteComparison';
import { orderQuoteRows } from '../quoteShiftOrder';

type Row = Record<string, any>;
export type QuoteReferenceLabel = 'Deleted' | 'Rejected';
export const isQuoteReference = (row: Row) => Boolean(row.quoteReferenceLabel);

export function comparisonForQuoteRow(row: Row, comparisons: ShiftComparison[]) {
  const byId = comparisons.find(item => item.originalData?.id != null && String(item.originalData.id) === String(row.id));
  if (byId) return byId;
  const byHistory = comparisons.find(item => item.historyData?.id != null &&
    (String(row.id) === `history-${item.historyData.id}` || row.clientRequestHistoryShiftId === item.historyData.id));
  if (byHistory) return byHistory;
  // Shift numbers are legacy display identifiers, not unique operational IDs.
  const legacy = comparisons.filter(item => item.originalData?.id == null && item.shiftNumber && String(item.shiftNumber) === String(row.shiftNumber));
  return legacy.length === 1 ? legacy[0] : undefined;
}

const decisionValue = (field: string, value: any) => {
  if (value === null || value === undefined || value === '') return value ?? null;
  if (field === 'startDate' || field === 'endDate') return value instanceof Date
    ? value.toISOString().slice(0, 10) : String(value).split('T')[0];
  return field === 'quantity' && Number.isFinite(Number(value)) ? Number(value) : value;
};
const sameDecisionValue = (field: string, left: any, right: any) =>
  JSON.stringify(decisionValue(field, left)) === JSON.stringify(decisionValue(field, right));

/** Decisions belong to the proposal, even after a later manual Admin edit. */
export function draftClientCellDecision(row: Row, comparison: ShiftComparison | undefined, fields: string[]) {
  const historyShiftId = Number(comparison?.historyData?.id);
  const changed = fields.filter(field => Boolean(comparison?.changes?.[field]));
  if (!changed.length || !Number.isSafeInteger(historyShiftId)) return undefined;
  const local = Array.isArray(row.clientRequestDraftDecisions) ? row.clientRequestDraftDecisions : [];
  const decisions = changed.map(field => local.find(item => item.historyShiftId === historyShiftId && item.field === field &&
    ['accept', 'reject'].includes(item.decision) &&
    sameDecisionValue(field, item.originalValue, comparison!.changes[field].base) &&
    sameDecisionValue(field, item.requestedValue, comparison!.changes[field].requested))?.decision);
  return decisions.every(value => value && value === decisions[0]) ? decisions[0] as 'accept' | 'reject' : undefined;
}

/** New request rows remain comparisons until explicitly accepted. */
export function clientRequestRowDecision(row: Row) {
  return (Array.isArray(row.clientRequestDraftDecisions) ? row.clientRequestDraftDecisions : []).find(item => item.field === '$row' &&
    item.historyShiftId === row.clientRequestHistoryShiftId && ['accept', 'reject'].includes(item.decision))?.decision;
}
export const isPendingRequestRow = (row: Row) => Boolean(row.clientRequestPending);
export const isInactiveQuoteRow = (row: Row) => isQuoteReference(row) || isPendingRequestRow(row);

const referenceSnapshot = (snapshot: Row): Row => ({
  ...snapshot,
  startDate: String(snapshot.startDate || '').split('T')[0],
  endDate: String(snapshot.endDate || '').split('T')[0],
  totals: snapshot.clientRatePending ? null : snapshot.totals ?? snapshot.total
});

/** Reference copies exist only in the render projection, never in the editable draft. */
export function clientRequestWorksheetRows(positions: Row[], comparisons: ShiftComparison[], membership?: { activeRowIds: string[] } | null, proposedRows: Row[] = [], historical = false) {
  const activeIds = membership ? new Set(membership.activeRowIds.map(String)) : null;
  const allRows = [...positions, ...proposedRows.filter(proposed => !positions.some(row =>
    String(row.id) === String(proposed.id) || row.clientRequestHistoryShiftId === proposed.clientRequestHistoryShiftId))];
  const requestRows = allRows.filter(row => (!historical || !activeIds?.has(String(row.id))) && String(row.id).startsWith('history-') && row.clientRequestHistoryShiftId &&
    clientRequestRowDecision(row) !== 'accept');
  const activeRows = positions.filter(row => !isInactiveQuoteRow(row) && !requestRows.includes(row) &&
    (comparisonForQuoteRow(row, comparisons)?.status !== 'deleted' || activeIds?.has(String(row.id))));
  const references: Row[] = comparisons.filter(item => item.status === 'deleted' && item.originalData &&
    !activeRows.some(row => String(row.id) === String(item.originalData.id))).map(item => ({
    ...referenceSnapshot(item.originalData), quoteReferenceLabel: 'Deleted', quoteReferenceKey: `deleted-${item.originalData.id}`
  }));
  const pending = requestRows.map(row => clientRequestRowDecision(row) === 'reject'
    ? { ...row, quoteReferenceLabel: 'Rejected', quoteReferenceKey: `rejected-${row.id}` }
    : { ...row, clientRequestPending: true });
  for (const row of activeRows) {
    const comparison = comparisonForQuoteRow(row, comparisons);
    const decision = draftClientCellDecision(row, comparison, ['startDate']);
    // A manual date override keeps its decision but no longer represents this pair.
    const applied = row.clientRequestDraftDecisions?.find(item => item.field === 'startDate' &&
      item.historyShiftId === Number(comparison?.historyData?.id));
    if (!decision || !sameDecisionValue('startDate', applied?.appliedValue, row.startDate)) continue;
    const snapshot = decision === 'accept' ? comparison!.originalData : comparison!.historyData;
    if (!snapshot) continue;
    references.push({ ...referenceSnapshot(snapshot), id: row.id, quoteReferenceLabel: decision === 'accept' ? 'Deleted' : 'Rejected',
      quoteReferenceKey: `date-${comparison!.historyData.id}-${row.id}-${decision}` });
  }
  return { activeRows, displayRows: orderQuoteRows([...activeRows, ...references, ...pending]) };
}
